import {
   createSelector,
   createAsyncThunk,
   createSlice,
   PayloadAction,
   createEntityAdapter,
} from "@reduxjs/toolkit"
import { resetAudioPlayback } from "./audioPlaybackSlice"
import {
   updateCellsByAlerts,
   removeAlertById,
   removeAlerts,
} from "./cellsSlice"
import { RootState } from "."
import { ApiError, ApiResponse } from "@/api"
import {
   AlertResponse,
   requestAlertsByLevel,
   requestClearAlert,
   requestClearAlerts,
   requestTriggerTestAlert,
   TriggerTestAlertResponse,
} from "@/api/AlertsService"
import { ClearAlertForm } from "@/components/AlertModal/ClearAlert/Form"
import { SoundFrequency } from "@/components/AlertSoundSettings/files"
import { HttpCodes } from "@/constants/globals"
import { Alert, AlertSource } from "@/types/alert"
import {
   sortAlerts,
   processThresholds,
   managePlayingAlertSound,
   isAlertSoundMuted,
   normalizeAlertLevelParents,
} from "@/utils/alerts"

const alertsAdapter = createEntityAdapter({
   selectId: (alert: Alert) => alert.id,
   sortComparer: sortAlerts,
})

interface AlertsState {
   loading: boolean
   error: number | null
   loadingClearAlert: boolean
   errorClearAlert: number | null
   loadingClearAlerts: boolean
   alertIdToClear: string | null
}

export const initialState: AlertsState &
   ReturnType<typeof alertsAdapter.getInitialState> = {
   ...alertsAdapter.getInitialState(),
   loading: false,
   error: null,
   loadingClearAlert: false,
   errorClearAlert: null,
   loadingClearAlerts: false,
   alertIdToClear: null,
}

export const fetchAlertsByLevel = createAsyncThunk(
   "alerts/fetchAlertsByLevel",
   async (levelId: string, thunkAPI) => {
      try {
         const response: ApiResponse<AlertResponse[]> =
            await requestAlertsByLevel(levelId)
         if (response.status === HttpCodes.OK) {
            thunkAPI.dispatch(
               updateCellsByAlerts({
                  response,
               })
            )
         }
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const clearAlert = createAsyncThunk(
   "alerts/clearAlert",
   async (
      {
         alertId,
         clearReport,
         levelId,
      }: { alertId: string; clearReport: ClearAlertForm; levelId?: string },
      thunkAPI
   ) => {
      try {
         thunkAPI.dispatch(setAlertIdToClear(alertId))
         const response: ApiResponse<void> = await requestClearAlert(
            alertId,
            clearReport,
            levelId
         )
         if (response.status === HttpCodes.OK) {
            thunkAPI.dispatch(
               removeAlertById({
                  alertId,
               })
            )
            thunkAPI.dispatch(
               removeAlertById({
                  alertId,
               })
            )
            thunkAPI.dispatch(resetAudioPlayback())
         }
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const clearAlerts = createAsyncThunk(
   "alerts/clearAlerts",
   async (
      { clearReport }: { clearReport: Partial<ClearAlertForm> },
      thunkAPI
   ) => {
      try {
         const response: ApiResponse<void> =
            await requestClearAlerts(clearReport)
         if (response.status === HttpCodes.OK) {
            thunkAPI.dispatch(removeAlerts())
         }
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const triggerTestAlert = createAsyncThunk(
   "alerts/triggerTestAlert",
   async (levelId: string, thunkAPI) => {
      try {
         const response: ApiResponse<TriggerTestAlertResponse> =
            await requestTriggerTestAlert(levelId)
         return response
      } catch (error) {
         if (error instanceof Error) {
            const apiError = error as unknown as ApiError
            return thunkAPI.rejectWithValue(apiError.status)
         }
         return thunkAPI.rejectWithValue(HttpCodes.INTERNAL_SERVER_ERROR)
      }
   }
)

export const alertsSlice = createSlice({
   name: "alerts",
   initialState,
   reducers: {
      resetAlerts: () => initialState,
      setAlertIdToClear: (state, action: PayloadAction<string>) => {
         state.alertIdToClear = action.payload
      },
   },
   extraReducers: (builder) => {
      builder
         .addCase(fetchAlertsByLevel.pending, (state) => {
            state.loading = true
            state.error = null
         })
         .addCase(
            fetchAlertsByLevel.fulfilled,
            (state, action: PayloadAction<ApiResponse<AlertResponse[]>>) => {
               state.loading = false
               const alerts = action.payload.data.map(
                  (alert, _) =>
                     ({
                        id: alert.id,
                        source: alert.source,
                        level: normalizeAlertLevelParents(alert.level),
                        inmate: alert.inmate,
                        priority: alert.priority,
                        timestamp: alert.timestamp,
                        vitals: alert.vitals,
                        alertConfiguration: alert.alertConfiguration,
                        ...processThresholds(alert.thresholds),
                        soundPlaying: false,
                     }) as Alert
               )
               const sortedAlerts = alerts.sort(sortAlerts)
               const newAlerts = sortedAlerts.filter(
                  (alert) => !state.ids.includes(alert.id)
               )

               const oldContinuousAlerts = sortedAlerts.filter(
                  (alert) =>
                     alert.alertConfiguration.sound.frequency ===
                     SoundFrequency.CONTINUOUS
               )

               const alertsWithSound = [
                  ...newAlerts,
                  ...oldContinuousAlerts,
               ].filter((alert) => !isAlertSoundMuted(alert))

               const soundManaged =
                  alertsWithSound.length > 0
                     ? managePlayingAlertSound(sortedAlerts, alertsWithSound)
                     : sortedAlerts
               alertsAdapter.setAll(state, soundManaged)
            }
         )
         .addCase(fetchAlertsByLevel.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as number
         })
         .addCase(clearAlert.pending, (state) => {
            state.loadingClearAlert = true
            state.errorClearAlert = null
         })
         .addCase(clearAlert.fulfilled, (state, action) => {
            alertsAdapter.removeOne(state, action.meta.arg.alertId)
            state.loadingClearAlert = false
            state.errorClearAlert = null
            state.alertIdToClear = null
         })
         .addCase(clearAlert.rejected, (state, action) => {
            state.loadingClearAlert = false
            state.errorClearAlert = action.payload as number
            state.alertIdToClear = null
         })
         .addCase(clearAlerts.pending, (state) => {
            state.loadingClearAlerts = true
            state.error = null
         })
         .addCase(clearAlerts.fulfilled, (state) => {
            alertsAdapter.removeAll(state)
            state.loadingClearAlerts = false
            state.error = null
         })
         .addCase(clearAlerts.rejected, (state, action) => {
            state.loadingClearAlerts = false
            state.error = action.payload as number
         })
   },
})

export const { resetAlerts, setAlertIdToClear } = alertsSlice.actions
export default alertsSlice.reducer
export const alertsSelectors = alertsAdapter.getSelectors<RootState>(
   (state) => state.alertsReducer
)

export const selectWearableAlertsByInmateId = createSelector(
   alertsSelectors.selectAll,
   (alerts) => {
      const alertsByInmateId: Record<string, Alert[]> = {}

      alerts.forEach((alert) => {
         if (alert.source !== AlertSource.WEARABLE) return
         const inmateId = alert.inmate?.id
         if (!inmateId) return

         if (!alertsByInmateId[inmateId]) {
            alertsByInmateId[inmateId] = []
         }
         alertsByInmateId[inmateId].push(alert)
      })

      return alertsByInmateId
   }
)
